/**
 * Animation Configuration
 * Centralized constants for all landing page animations
 */

export const ANIMATION_CONFIG = {
  camera: {
    /** Default camera distance from origin */
    defaultDistance: 350,
    /** Zoom distance when focused on an agent/platform */
    focusZoom: 100,
    /** Zoom distance during demo mode (more zoomed out for overview) */
    demoZoom: 180,
    /** Duration for camera focus transitions */
    focusDuration: 2.2,
    /** Duration for camera reset transitions */
    resetDuration: 1.4,
    /** Vertical offset multiplier for camera position */
    verticalOffsetMultiplier: 0.25,
  },
  beams: {
    /** Base speed for data beam travel along bezier curves */
    baseSpeed: 0.35,
    /** Random variance added to beam speed */
    speedVariance: 0.1,
    /** Frequency of beam opacity pulsing */
    pulseFrequency: 6,
    /** Frequency of glow sprite pulsation */
    glowPulseFrequency: 8,
    /** Base opacity for beams */
    baseOpacity: 0.7,
    /** Opacity pulse amplitude */
    opacityPulseAmplitude: 0.3,
    /** Maximum trail particles per beam */
    maxTrailParticles: 8,
  },
  orbit: {
    /** Speed of agent orbital rotation (counter-clockwise) */
    agentSpeed: 0.0003,
    /** Speed of platform orbital rotation (clockwise) */
    platformSpeed: 0.0003,
    /** Amplitude of vertical wave motion */
    verticalWaveAmplitude: 12,
    /** Frequency multiplier for vertical wave */
    verticalWaveFrequency: 3,
  },
  demo: {
    /** Duration of each demo step in milliseconds */
    stepDuration: 30000,
    /** Delay before auto-advancing to next step */
    autoAdvanceDelay: 30000,
  },
  prezi: {
    /** Zoom-out anticipation duration (pullback before moving) */
    anticipationDuration: 0.25,
    /** Amount to pull back camera during anticipation */
    anticipationPullback: 40,
    /** Base travel duration (scaled by distance) */
    travelDurationBase: 0.8,
    /** Maximum travel duration cap */
    travelDurationMax: 1.4,
    /** Distance scaling factor for travel duration */
    travelDistanceScale: 0.003,
    /** Final settle/zoom-in duration */
    settleDuration: 0.25,
    /** Settle zoom amount (camera moves closer) */
    settleZoom: 12,
    /** Height offset above target */
    heightOffset: 35,
    /** Default distance from target */
    defaultDistance: 100,
    /** Demo mode distance (further back for overview) */
    demoDistance: 180,
    /** Arc height as proportion of distance (lower = flatter arc) */
    arcHeightFactor: 0.15,
    /** Minimum arc height */
    arcHeightMin: 30,
    /** Maximum arc height */
    arcHeightMax: 80,
  },
  entrance: {
    /** Stagger delay between node entrance animations */
    stagger: 0.05,
    /** Duration of scale-in animation */
    duration: 0.5,
    /** Easing function for entrance animations */
    ease: 'back.out(1.7)',
  },
  pulse: {
    /** Core logo pulse frequency */
    coreFrequency: 2,
    /** Core logo pulse amplitude */
    coreAmplitude: 0.03,
    /** Ring pulse frequency */
    ringFrequency: 1.5,
    /** Ring pulse amplitude */
    ringAmplitude: 0.05,
  },
};

/** Camera animation easing presets - Prezi-style smooth motion */
export const CAMERA_EASINGS = {
  // Anticipation: quick ease out for responsive feel
  anticipation: 'power2.out',
  // Travel: smooth in-out for cinematic movement
  travel: 'power2.inOut',
  // Settle: slight overshoot for satisfying landing
  settle: 'back.out(1.2)',
  // Legacy fallbacks
  focus: 'power2.inOut',
  reset: 'power2.inOut',
  orbit: 'none',
};

/** Bezier curve control point calculations */
export const BEZIER_CONFIG = {
  /** Control point height offset for smooth arcs */
  controlPointHeight: 50,
  /** Midpoint elevation for bezier curves */
  midpointElevation: 0.5,
};

/**
 * Reduced motion configuration for accessibility
 * When user prefers reduced motion, use these simpler values
 */
export const REDUCED_MOTION_CONFIG = {
  prezi: {
    anticipationDuration: 0.05,
    anticipationPullback: 0,
    travelDurationBase: 0.2,
    travelDurationMax: 0.3,
    travelDistanceScale: 0,
    settleDuration: 0.05,
    settleZoom: 0,
    heightOffset: 35,
    defaultDistance: 100,
    demoDistance: 180,
    arcHeightFactor: 0,
    arcHeightMin: 0,
    arcHeightMax: 0,
  },
  sprite: {
    selectionDuration: 0.1,
    deselectionDuration: 0.1,
  },
};

/** Check if user prefers reduced motion */
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/** Get animation config with reduced motion awareness */
export const getAnimationConfig = () => {
  if (prefersReducedMotion()) {
    return {
      ...ANIMATION_CONFIG,
      prezi: REDUCED_MOTION_CONFIG.prezi,
    };
  }
  return ANIMATION_CONFIG;
};

/** Reduced motion easing - simple linear for accessibility */
export const REDUCED_MOTION_EASINGS = {
  anticipation: 'none',
  travel: 'power1.inOut',
  settle: 'power1.out',
  focus: 'power1.inOut',
  reset: 'power1.inOut',
  orbit: 'none',
};

/** Get camera easings with reduced motion awareness */
export const getCameraEasings = () => {
  if (prefersReducedMotion()) {
    return REDUCED_MOTION_EASINGS;
  }
  return CAMERA_EASINGS;
};

export default ANIMATION_CONFIG;
